import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RadialNav.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface RadialNavBranch {
  id: string;
  label: string;
  icon: string;
  weight: number;
  /** Child nodes that bloom outward when this branch is active */
  children?: RadialNavBranch[];
  /** Optional selectable values for leaf nodes */
  values?: string[];
  /** Currently selected value (controlled) */
  selectedValue?: string;
}

export interface RadialNavProps {
  data: Record<string, any>;
  onSelect: (branchId: string) => void;
  /** Called whenever a value is toggled on a node — wires into central state */
  onChange?: (branchId: string, key: string, value: string) => void;
  hubLabel?: string;
  branches?: RadialNavBranch[];
}

/* ------------------------------------------------------------------ */
/*  Default branch data with nested children                           */
/* ------------------------------------------------------------------ */

const DEFAULT_BRANCHES: RadialNavBranch[] = [
  {
    id: 'fabric',
    label: 'Fabric',
    icon: '🏠',
    weight: 4,
    children: [
      { id: 'insulation', label: 'Insulation', icon: '🧱', weight: 2, values: ['none', 'partial', 'full'] },
      { id: 'windows', label: 'Windows', icon: '🪟', weight: 2, values: ['single', 'double', 'triple'] },
      { id: 'draughtproofing', label: 'Draught-proofing', icon: '🌬️', weight: 1, values: ['none', 'partial', 'full'] },
    ],
  },
  {
    id: 'distribution',
    label: 'Distribution',
    icon: '🛤️',
    weight: 3,
    children: [
      { id: 'pipeSize', label: 'Pipe Size', icon: '🔧', weight: 2, values: ['22mm', '28mm', 'unknown'] },
      { id: 'planType', label: 'Plan Type', icon: '📋', weight: 2, values: ['y_plan', 's_plan', 'unknown'] },
      { id: 'filtration', label: 'Filtration', icon: '🧹', weight: 1, values: ['none', 'magnetic', 'laminar'] },
    ],
  },
  {
    id: 'hotwater',
    label: 'Hot Water',
    icon: '🚿',
    weight: 3,
    children: [
      { id: 'cylinder', label: 'Cylinder', icon: '🪣', weight: 2, values: ['none', 'vented', 'unvented'] },
      { id: 'flowRate', label: 'Flow Rate', icon: '💧', weight: 1, values: ['low', 'medium', 'high'] },
    ],
  },
  {
    id: 'heating',
    label: 'Heating',
    icon: '🔥',
    weight: 4,
    children: [
      { id: 'boilerType', label: 'Boiler Type', icon: '🏭', weight: 3, values: ['combi', 'system', 'regular', 'back_boiler'] },
      { id: 'boilerAge', label: 'Boiler Age', icon: '📅', weight: 2, values: ['<5', '5-10', '10-15', '15+'] },
      { id: 'emitters', label: 'Emitters', icon: '🌡️', weight: 2, values: ['radiators', 'underfloor', 'mixed'] },
    ],
  },
  {
    id: 'ventilation',
    label: 'Ventilation',
    icon: '💨',
    weight: 2,
    children: [
      { id: 'ventType', label: 'Vent Type', icon: '🌀', weight: 1, values: ['natural', 'extract', 'mvhr'] },
    ],
  },
  {
    id: 'controls',
    label: 'Controls',
    icon: '🎛️',
    weight: 3,
    children: [
      { id: 'thermostat', label: 'Thermostat', icon: '🌡️', weight: 2, values: ['none', 'basic', 'programmable', 'smart'] },
      { id: 'trvs', label: 'TRVs', icon: '🔘', weight: 1, values: ['none', 'some', 'all'] },
    ],
  },
  {
    id: 'renewable',
    label: 'Renewable',
    icon: '☀️',
    weight: 3,
    children: [
      { id: 'solarPV', label: 'Solar PV', icon: '⚡', weight: 2, values: ['none', 'installed', 'planned'] },
      { id: 'heatPump', label: 'Heat Pump', icon: '♨️', weight: 2, values: ['none', 'ashp', 'gshp'] },
    ],
  },
  {
    id: 'storage',
    label: 'Storage',
    icon: '🔋',
    weight: 2,
    children: [
      { id: 'battery', label: 'Battery', icon: '🔋', weight: 1, values: ['none', 'installed', 'planned'] },
      { id: 'thermal', label: 'Thermal Store', icon: '🫙', weight: 1, values: ['none', 'installed'] },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Progress calculation                                               */
/* ------------------------------------------------------------------ */

function calculateProgress(branchData: any): number {
  if (!branchData || typeof branchData !== 'object') return 0;
  const fields = Object.values(branchData);
  if (fields.length === 0) return 0;
  const filled = fields.filter(v => v !== null && v !== undefined && v !== '');
  return filled.length / fields.length;
}

/* ------------------------------------------------------------------ */
/*  Memoised SVG progress ring                                         */
/* ------------------------------------------------------------------ */

const CIRCUMFERENCE = 2 * Math.PI * 25; // ≈ 157

interface ProgressRingProps {
  progress: number;
  icon: string;
}

const ProgressRing = React.memo<ProgressRingProps>(function ProgressRing({ progress, icon }) {
  const offset = CIRCUMFERENCE - CIRCUMFERENCE * progress;
  return (
    <svg width="60" height="60" className="radial-nav__progress-ring">
      <circle cx="30" cy="30" r="25" fill="white" stroke="#e2e8f0" strokeWidth="4" />
      <circle
        cx="30"
        cy="30"
        r="25"
        fill="none"
        stroke="#10b981"
        strokeWidth="4"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
        className="radial-nav__progress-circle"
        transform="rotate(-90 30 30)"
      />
      <text x="30" y="38" textAnchor="middle" fontSize="20" className="radial-nav__icon">
        {icon}
      </text>
    </svg>
  );
});

/* ------------------------------------------------------------------ */
/*  Memoised individual radial node (recursive)                        */
/* ------------------------------------------------------------------ */

interface RadialNodeProps {
  branch: RadialNavBranch;
  angle: number;
  radius: number;
  index: number;
  depth: number;
  progress: number;
  isActive: boolean;
  onSelect: (branchId: string) => void;
  onChange?: (branchId: string, key: string, value: string) => void;
  data: Record<string, any>;
  activeNodes: Set<string>;
  onToggleActive: (branchId: string) => void;
}

const RadialNode = React.memo<RadialNodeProps>(function RadialNode({
  branch,
  angle,
  radius,
  index,
  depth,
  progress,
  isActive,
  onSelect,
  onChange,
  data,
  activeNodes,
  onToggleActive,
}) {
  const hasChildren = branch.children && branch.children.length > 0;
  const childRadius = radius + 100; // each depth level pushes 100px further out
  const childSpread = 30; // degrees between child nodes

  const handleClick = useCallback(() => {
    onSelect(branch.id);
    if (hasChildren) {
      onToggleActive(branch.id);
    }
  }, [branch.id, hasChildren, onSelect, onToggleActive]);

  const handleValueSelect = useCallback(
    (value: string) => {
      if (onChange) {
        onChange(branch.id, branch.id, value);
      }
    },
    [branch.id, onChange],
  );

  // Calculate the position using CSS transform
  const style: React.CSSProperties = {
    transform: `rotate(${angle}deg) translate(${radius}px) rotate(-${angle}deg)`,
  };

  return (
    <>
      <motion.div
        className={`radial-nav__spike ${isActive ? 'radial-nav__spike--active' : ''} ${depth > 0 ? 'radial-nav__spike--child' : ''}`}
        initial={{ opacity: 0, scale: 0.3 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.3 }}
        whileHover={{ scale: 1.1 }}
        transition={{ delay: index * 0.04, type: 'spring', stiffness: 260, damping: 20 }}
        onClick={handleClick}
        style={style}
      >
        <div className="radial-nav__spike-content">
          <ProgressRing progress={progress} icon={branch.icon} />
          <span className="radial-nav__label">{branch.label}</span>

          {/* Leaf-node value selector (shown inline when active and has values) */}
          {isActive && branch.values && (
            <motion.div
              className="radial-nav__values"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {branch.values.map(v => (
                <button
                  key={v}
                  className={`radial-nav__value-btn ${branch.selectedValue === v ? 'radial-nav__value-btn--selected' : ''}`}
                  onClick={e => {
                    e.stopPropagation();
                    handleValueSelect(v);
                  }}
                >
                  {v}
                </button>
              ))}
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Recursively render children when this node is active */}
      <AnimatePresence>
        {isActive && hasChildren &&
          branch.children!.map((child, ci) => {
            const totalChildren = branch.children!.length;
            // Center children around the parent angle
            const childAngle =
              angle + (ci - (totalChildren - 1) / 2) * childSpread;
            const childProgress = calculateProgress(data[child.id]);
            const childIsActive = activeNodes.has(child.id);

            return (
              <RadialNode
                key={child.id}
                branch={child}
                angle={childAngle}
                radius={childRadius}
                index={ci}
                depth={depth + 1}
                progress={childProgress}
                isActive={childIsActive}
                onSelect={onSelect}
                onChange={onChange}
                data={data}
                activeNodes={activeNodes}
                onToggleActive={onToggleActive}
              />
            );
          })}
      </AnimatePresence>
    </>
  );
});

/* ------------------------------------------------------------------ */
/*  Main RadialNav component                                           */
/* ------------------------------------------------------------------ */

export const RadialNav: React.FC<RadialNavProps> = ({
  data,
  onSelect,
  onChange,
  hubLabel = 'THE HOME',
  branches = DEFAULT_BRANCHES,
}) => {
  const [activeNodes, setActiveNodes] = useState<Set<string>>(new Set());

  const handleToggleActive = useCallback((branchId: string) => {
    setActiveNodes(prev => {
      const next = new Set(prev);
      if (next.has(branchId)) {
        next.delete(branchId);
      } else {
        next.add(branchId);
      }
      return next;
    });
  }, []);

  // Determine if any branch is expanded (for dynamic container sizing)
  const hasExpandedChildren = useMemo(() => {
    return branches.some(
      b => activeNodes.has(b.id) && b.children && b.children.length > 0,
    );
  }, [activeNodes, branches]);

  return (
    <div className={`radial-nav ${hasExpandedChildren ? 'radial-nav--expanded' : ''}`}>
      {/* Central Hub */}
      <div className="radial-nav__hub">
        <span className="radial-nav__hub-label">{hubLabel}</span>
      </div>

      {/* Radial Spikes (recursive) */}
      <div className="radial-nav__spikes">
        <AnimatePresence>
          {branches.map((branch, i) => {
            const angle = (i * 360) / branches.length;
            const progress = calculateProgress(data[branch.id]);
            const isActive = activeNodes.has(branch.id);

            return (
              <RadialNode
                key={branch.id}
                branch={branch}
                angle={angle}
                radius={200}
                index={i}
                depth={0}
                progress={progress}
                isActive={isActive}
                onSelect={onSelect}
                onChange={onChange}
                data={data}
                activeNodes={activeNodes}
                onToggleActive={handleToggleActive}
              />
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};
